// DataTable — thin MUI Table wrapper with server-side pagination (Protocol B).
//
// Shape: takes columns + rows + paginationModel + total. Caller drives state
// via usePagedQuery (or local useState for ad-hoc cases).
//
// Note: spec mentions MUI DataGrid, but @mui/x-data-grid is not installed in
// the admin workspace. We fulfil the same contract with MUI <Table> +
// <TablePagination>: server-side paging (page+pageSize), rowCount, controlled
// model — the caller's `onPaginationModelChange` re-issues the list query.
//
// pageSizeOptions = [5, 8, 20, 50, 100] (Dashboard A02 selects 5).

import { type ReactNode } from "react";
import {
  Box,
  Checkbox,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
  CircularProgress,
} from "@mui/material";
import { tokens } from "@findgeeks/ui-tokens";

export interface DataTableColumn<T> {
  field: string;
  headerName: string;
  width?: number | string;
  align?: "left" | "right" | "center";
  /** Custom renderer; defaults to row[field]. */
  render?: (row: T) => ReactNode;
}

export interface DataTablePaginationModel {
  page: number; // zero-based
  pageSize: number;
}

export interface DataTableProps<T> {
  columns: DataTableColumn<T>[];
  rows: T[];
  rowCount: number;
  paginationModel: DataTablePaginationModel;
  onPaginationModelChange: (m: DataTablePaginationModel) => void;
  getRowId: (row: T) => string;
  /** Show a checkbox column for bulk selection. */
  checkboxSelection?: boolean;
  selectedIds?: string[];
  onSelectionChange?: (ids: string[]) => void;
  /** Right-side row actions (rendered inside the row). */
  rowActions?: (row: T) => ReactNode;
  loading?: boolean;
  emptyMessage?: string;
  pageSizeOptions?: number[];
  /** Optional dense override (default true). */
  dense?: boolean;
  /** When false, omits the outer Paper border + radius — useful when the
   *  table is rendered inside another card (e.g. Dashboard's combined
   *  Event/Client wrapper). Default true. */
  bordered?: boolean;
}

export function DataTable<T>(props: DataTableProps<T>) {
  const {
    columns,
    rows,
    rowCount,
    paginationModel,
    onPaginationModelChange,
    getRowId,
    checkboxSelection = false,
    selectedIds = [],
    onSelectionChange,
    rowActions,
    loading = false,
    emptyMessage = "No rows",
    pageSizeOptions = [5, 10, 20, 50, 100],
    dense = true,
    bordered = true,
  } = props;

  const allSelected =
    rows.length > 0 && rows.every((r) => selectedIds.includes(getRowId(r)));
  const someSelected =
    rows.some((r) => selectedIds.includes(getRowId(r))) && !allSelected;

  const toggleAll = () => {
    if (!onSelectionChange) return;
    if (allSelected) {
      onSelectionChange(
        selectedIds.filter((id) => !rows.find((r) => getRowId(r) === id)),
      );
    } else {
      const ids = rows.map(getRowId);
      const merged = Array.from(new Set([...selectedIds, ...ids]));
      onSelectionChange(merged);
    }
  };

  const toggleRow = (id: string) => {
    if (!onSelectionChange) return;
    if (selectedIds.includes(id)) {
      onSelectionChange(selectedIds.filter((x) => x !== id));
    } else {
      onSelectionChange([...selectedIds, id]);
    }
  };

  return (
    <Paper
      elevation={0}
      sx={{
        border: bordered ? `1px solid ${tokens.colors.admin.border}` : 0,
        borderRadius: bordered ? 1 : 0,
        overflow: "hidden",
        background: "transparent",
      }}
    >
      <TableContainer>
        <Table size={dense ? "small" : "medium"}>
          <TableHead>
            <TableRow>
              {checkboxSelection && (
                <TableCell padding="checkbox">
                  <Checkbox
                    indeterminate={someSelected}
                    checked={allSelected}
                    onChange={toggleAll}
                    aria-label="Select all rows"
                  />
                </TableCell>
              )}
              {columns.map((col) => (
                <TableCell
                  key={col.field}
                  align={col.align ?? "left"}
                  style={{ width: col.width }}
                  sx={{
                    fontSize: tokens.font.sizes.bodySm,
                    fontWeight: tokens.font.weights.semibold,
                    color: tokens.colors.admin.textHeading,
                    background: tokens.colors.admin.bg,
                  }}
                >
                  {col.headerName}
                </TableCell>
              ))}
              {rowActions && (
                <TableCell
                  align="right"
                  sx={{
                    fontSize: tokens.font.sizes.bodySm,
                    fontWeight: tokens.font.weights.semibold,
                    background: tokens.colors.admin.bg,
                  }}
                >
                  Manage
                </TableCell>
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {loading && rows.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={
                    columns.length +
                    (checkboxSelection ? 1 : 0) +
                    (rowActions ? 1 : 0)
                  }
                  align="center"
                  sx={{ py: 4 }}
                >
                  <CircularProgress size={20} />
                </TableCell>
              </TableRow>
            ) : rows.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={
                    columns.length +
                    (checkboxSelection ? 1 : 0) +
                    (rowActions ? 1 : 0)
                  }
                  align="center"
                  sx={{ py: 4 }}
                >
                  <Typography
                    color="text.secondary"
                    sx={{ fontSize: tokens.font.sizes.bodySm }}
                  >
                    {emptyMessage}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              rows.map((row) => {
                const id = getRowId(row);
                return (
                  <TableRow key={id} hover>
                    {checkboxSelection && (
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={selectedIds.includes(id)}
                          onChange={() => toggleRow(id)}
                          aria-label={`Select row ${id}`}
                        />
                      </TableCell>
                    )}
                    {columns.map((col) => (
                      <TableCell
                        key={col.field}
                        align={col.align ?? "left"}
                        sx={{ fontSize: tokens.font.sizes.bodySm }}
                      >
                        {col.render
                          ? col.render(row)
                          : ((row as Record<string, unknown>)[
                              col.field
                            ] as ReactNode) ?? ""}
                      </TableCell>
                    ))}
                    {rowActions && (
                      <TableCell align="right">
                        <Box
                          sx={{
                            display: "flex",
                            gap: 0.5,
                            justifyContent: "flex-end",
                          }}
                        >
                          {rowActions(row)}
                        </Box>
                      </TableCell>
                    )}
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        component="div"
        count={rowCount}
        page={paginationModel.page}
        rowsPerPage={paginationModel.pageSize}
        rowsPerPageOptions={pageSizeOptions}
        onPageChange={(_, newPage) =>
          onPaginationModelChange({ ...paginationModel, page: newPage })
        }
        onRowsPerPageChange={(e) =>
          onPaginationModelChange({
            page: 0,
            pageSize: Number(e.target.value),
          })
        }
        labelRowsPerPage="Items per page:"
      />
    </Paper>
  );
}
